// TechnicalView - one view of the technical documentation, as its own tab:
// summary strip, caveats above the drawing, flags, the register isometric
// and the schedule, with linked highlighting between them (guide 18.6).
// Owner side reviews it: edit rows (kept as "edited" through
// regenerations), accept Claude flags, move it from draft to published,
// regenerate when the building data has moved on.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Dropdown, Input, InputNumber, Modal, Popconfirm, Select, Skeleton, Tag, Tooltip, message } from "antd";
import { DownOutlined, DownloadOutlined, FileExcelOutlined, PrinterOutlined, ReloadOutlined } from "@ant-design/icons";
import { downloadAuthed, printElement } from "../printExport";
import RegisterIsometric from "./RegisterIsometric";
import ReconcilePanel from "./ReconcilePanel";
import ModelLinkModal, { openInModel } from "./ModelLinkModal";
import TrackingPanel from "./TrackingPanel";
import SheetPages from "./SheetPages";
import { useTrackingEvents } from "./liveTracking";
import FacetBar from "./FacetBar";
import { applyFilter, emptyReason, filterFromHash, filterToHash, isEmptyFilter, tally } from "./facets";
// root-8e's (guide 21): renders nothing outside the owner company, so no permission check here.
import EstimatePanel from "components/Coordination/EstimatePanel";
// root-8e's (guide 21.7): the kickoff pack and the glossary "?" - each renders nothing for anyone the server refuses.
import MeetingPackButton from "components/Coordination/MeetingPackButton";
import GlossaryTermRaw from "components/Coordination/GlossaryTerm";
import Contained from "./Contained";
import ViewPdfActions from "./ViewPdfActions";
import { groupRows } from "./groupRows";
import { ftInText, openNewView, zonePageUrl } from "../glanceModel";
import VersionChip from "components/Documents/VersionChip";
import { useHistory } from "react-router-dom";
import EvidenceThumbRaw from "components/Coordination/EvidenceThumb";   // root-8e: the tag cut from its sheet   // root-f5: server-rendered PDF of this view (carries the #filter)
import {
  BASIS_LABEL, STATUS_LABEL, downloadCsv, exportQuery, fmtFt, levelName, rowLevel, runColor, viewsApi,
} from "./viewModel";

const LENGTH_BASES = ["model", "traced", "estimate", "assumed", "none"];

function EditRow({ row, onCancel, onSave }) {
  const [draft, setDraft] = useState(row);
  useEffect(() => setDraft(row), [row]);
  if (!row) return null;
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  return (
    <Modal open title={`Edit ${row.id}`} okText="Save" onCancel={onCancel} width={640}
           onOk={() => {
             const changes = {};
             ["type", "qty", "size", "height", "lengthFt", "lengthBasis", "from", "to", "serves", "explanation"].forEach((k) => {
               if ((draft[k] == null ? "" : draft[k]) !== (row[k] == null ? "" : row[k])) changes[k] = draft[k];
             });
             onSave(changes);
           }}>
      <p className="g-tsub">Edited rows are marked with your name and are never overwritten when the view is regenerated.</p>
      <div className="g-form">
        <label>Type<Input value={draft.type} onChange={(e) => set("type", e.target.value)} /></label>
        <label>Quantity<InputNumber min={0} value={draft.qty} onChange={(v) => set("qty", v)} /></label>
        <label>Size<Input value={draft.size} onChange={(e) => set("size", e.target.value)} /></label>
        <label>Height<Input value={draft.height} onChange={(e) => set("height", e.target.value)} /></label>
        <label>Length each (ft)<InputNumber min={0} value={draft.lengthFt} onChange={(v) => set("lengthFt", v == null ? null : v)} /></label>
        <label>Length basis
          <Select value={draft.lengthBasis} onChange={(v) => set("lengthBasis", v)} options={LENGTH_BASES.map((b) => ({ value: b, label: BASIS_LABEL[b] }))} />
        </label>
        <label>From<Input value={draft.from} onChange={(e) => set("from", e.target.value)} /></label>
        <label>To<Input value={draft.to} onChange={(e) => set("to", e.target.value)} /></label>
        <label>Serves<Input value={draft.serves} onChange={(e) => set("serves", e.target.value)} /></label>
        <label className="g-wide">Explanation<Input.TextArea rows={3} value={draft.explanation} onChange={(e) => set("explanation", e.target.value)} /></label>
      </div>
    </Modal>
  );
}

// The field's four statuses and their colours (the tracking board's).
// Guide 18.6: the ceiling a run sits above - read off the ceiling plan, or
// assumed (from similar rooms, or the view's own height), and said so.
const ceilingText = (r) => (r && r.ceilingFt != null
  ? (r.ceilingBasis === "read" ? `Ceiling ${ftInText(r.ceilingFt)}` : `Ceiling about ${ftInText(r.ceilingFt)}, assumed`)
  : "");
const FIELD_LABEL = { projected: "Projected", procured: "Procured", being_installed: "Being installed", installed: "Installed" };
const FIELD_COLOR = { projected: "default", procured: "cyan", being_installed: "blue", installed: "green" };

// Which glossary entry explains a row type (root-8e's one list, GET /glossary).
const GlossaryTerm = (props) => <Contained name="glossary"><GlossaryTermRaw {...props} /></Contained>;
const EvidenceThumb = (props) => <Contained name="evidence"><EvidenceThumbRaw {...props} /></Contained>;
const termOf = (type) => {
  const t = String(type || "");
  if (/ez-?path/i.test(t)) return "ezpath";
  if (/riser/i.test(t)) return "riser";
  if (/home\s?run/i.test(t)) return "homerun";
  if (/backbone/i.test(t)) return "backbone";
  if (/\bDAS\b/.test(t)) return "das";
  if (/sleeve/i.test(t)) return "sleeve";
  return "";
};

// A constraint flag linked to a checklist item shows the item's live state.
const CHECK_LABEL = { open: "Checklist: open", in_progress: "Checklist: in progress", pending_review: "Checklist: pending review", complete: "Checklist: done", abandoned: "Checklist: dropped" };
const CHECK_COLOR = { open: "default", in_progress: "blue", pending_review: "gold", complete: "green", abandoned: "default" };

// Removing a view is technicalDocsDelete (2026-10-14); out here so the
// view's body keeps one branch fewer (the hooks lint counts paths).
function RemoveViewButton({ show, onConfirm }) {
  if (!show) return null;
  return (
    <Popconfirm title="Remove this view and all its versions from the documentation?" okText="Remove" onConfirm={onConfirm}>
      <Button size="small" danger>Remove</Button>
    </Popconfirm>
  );
}

export default function TechnicalView({ projectId, manifest, viewId, onChanged, onDeleted }) {
  const [view, setView] = useState(null);
  const [error, setError] = useState("");
  const [version, setVersion] = useState(null);
  const [versions, setVersions] = useState([]);
  // The ONE filter (guide 20.1), kept in the URL hash (guide 20.6) so a view
  // opened from a link shows exactly what the sender was looking at.
  const history = useHistory();
  const [filter, setFilter] = useState(() => filterFromHash(typeof window !== "undefined" ? window.location.hash : ""));
  const [statusByRow, setStatusByRow] = useState(null);
  // One pick for the tracking sheets and the tracking table (guide 19.2).
  const [picked, setPicked] = useState([]);
  // Bumped when someone else changes a status on this view (liveTracking): the
  // sheets and the tracking table re-read their states.
  const [liveTick, setLiveTick] = useState(0);
  useTrackingEvents(projectId, viewId, () => setLiveTick((t) => t + 1));
  const [hover, setHover] = useState(null);
  // Levels whose DAS remote units are shown one by one (groupRows).
  const [openGroups, setOpenGroups] = useState(() => new Set());
  const [tip, setTip] = useState(null);
  const [editing, setEditing] = useState(null);
  const [linking, setLinking] = useState(null);
  const [busy, setBusy] = useState(false);
  const tableRef = useRef(null);
  const rootRef = useRef(null);

  const load = useCallback(() => {
    setError("");
    viewsApi(projectId, `/${encodeURIComponent(viewId)}${version ? `?version=${version}` : ""}`)
      .then(setView).catch((e) => setError(e.message));
    viewsApi(projectId, `/${encodeURIComponent(viewId)}/versions`).then(setVersions).catch(() => setVersions([]));
  }, [projectId, viewId, version]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    try {
      const hash = filterToHash(filter);
      if (hash !== window.location.hash) window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${hash}`);
    } catch (e) { /* the filter still works without the URL */ }
  }, [filter]);

  const rows = useMemo(() => (view ? view.rows : []), [view]);
  const types = useMemo(() => [...new Set(rows.map((r) => r.type))], [rows]);
  // Status is a facet only once the view is tracked (guide 20.1: tracking mode only).
  const facetCtx = useMemo(() => (statusByRow ? { statusOf: (r) => statusByRow.get(r.id) || "projected" } : null), [statusByRow]);
  const visible = useMemo(() => applyFilter(rows, filter, facetCtx), [rows, filter, facetCtx]);
  const visibleIds = useMemo(() => new Set(visible.map((r) => r.id)), [visible]);
  const shownIds = isEmptyFilter(filter) ? undefined : visible.map((r) => r.id);
  // Rows read from a contractor's marked-up tracker carry the stage as marked
  // and the field status it maps to; the schedule shows both side by side.
  const staged = rows.some((r) => r.stage || r.fieldStatus);
  // One register row; a unit shown under its level's group is indented.
  const rowTr = (r, child) => (
                <tr key={r.id} data-id={r.id} tabIndex={0} className={[hover === r.id ? "is-hi" : "", child ? "g-child" : ""].filter(Boolean).join(" ") || undefined}
                    onMouseEnter={() => setHover(r.id)} onMouseLeave={() => setHover(null)} onFocus={() => setHover(r.id)} onBlur={() => setHover(null)}>
                  <td>
                    {r.id}
                    {canEdit && <div className="g-noprint"><button type="button" className="g-linkbtn" onClick={() => setEditing(r)}>Edit</button></div>}
                    {r.level && r.zone != null && r.zone >= 0 && (
                      <div className="g-noprint">
                        {/* The zone page, filtered to this view's size and named after it (the only way a size filter reaches that page). */}
                        <a className="g-linkbtn" target="_blank" rel="noopener noreferrer"
                           href={zonePageUrl(projectId, { level: r.level, zone: r.zone, sys: null, size: (view.request && view.request.filter && view.request.filter.size) || "", view: view.title, only: true })}>
                          Zone
                        </a>
                      </div>
                    )}
                    <div className="g-noprint">
                      {((r.links && r.links.modelElements) || []).length > 0 ? (
                        <button type="button" className="g-linkbtn" title={`${r.links.modelElements.length} model element(s): ${r.links.modelElements.map((m) => m.type || m.category).join(", ")}`}
                                onClick={() => (r.links.modelElements.length === 1 ? openInModel(projectId, r.links.modelElements[0].uniqueId) : setLinking(r))}>
                          3D ({r.links.modelElements.length})
                        </button>
                      ) : canEdit ? (
                        <button type="button" className="g-linkbtn" onClick={() => setLinking(r)}>Link to model</button>
                      ) : null}
                    </div>
                  </td>
                  <td>{rowLevel(manifest, r)}</td>
                  <td>{r.type}{termOf(r.type) ? <GlossaryTerm id={termOf(r.type)} /> : null}{r.size ? <div className="g-tsub">{r.size}</div> : null}</td>
                  {staged && (
                    <>
                      <td>{r.stage || ""}</td>
                      <td>
                        {r.fieldStatus ? <Tag color={FIELD_COLOR[r.fieldStatus]}>{FIELD_LABEL[r.fieldStatus] || r.fieldStatus}</Tag> : null}
                        {r.statusReview ? <div className="g-tsub">{r.statusReview}</div> : null}
                      </td>
                    </>
                  )}
                  <td className="g-num">{r.qty}</td>
                  <td>{r.height}{ceilingText(r) ? <div className="g-tsub" title={r.ceilingNote || "Read off the ceiling plan"}>{ceilingText(r)}</div> : null}</td>
                  <td className="g-num">
                    {fmtFt(r.lengthFt)}
                    <div className="g-basis">{BASIS_LABEL[r.lengthBasis] || r.lengthBasis}{r.lengthNote ? `: ${r.lengthNote}` : ""}</div>
                    {r.review === "edited" && r.computed && Object.keys(r.computed).length > 0 && (
                      <div className="g-basis">Computed: {Object.entries(r.computed).map(([k, v]) => `${k} ${v == null ? "blank" : v}`).join(", ")}</div>
                    )}
                  </td>
                  <td className="g-num">{r.lengthFt != null ? fmtFt(r.lengthFt * (r.qty || 1)) : ""}</td>
                  <td>{r.from}</td>
                  <td>{r.to}{r.serves && r.serves !== r.to ? <div className="g-tsub">Serves {r.serves}</div> : null}</td>
                  <td>{(r.keynotes || []).join(", ")}</td>
                  <td>
                    {(r.sources || []).map((x) => x.sheet || x.docId).join(", ")}
                    {(() => { const ev = (r.sources || []).find((x) => x.box && x.docId); return ev ? <div className="g-noprint"><EvidenceThumb projectId={projectId} evidence={{ docId: ev.docId, revisionNumber: ev.revisionNumber, page: ev.page, box: ev.box }} /></div> : null; })()}
                  </td>
                  <td className="g-expl">
                    {r.explanation}
                    {r.explanationBy === "claude" && r.review === "pending" && r.explanation && <span className="g-badge" title="Explanation written automatically; not yet reviewed">Pending</span>}
                    {r.review === "edited" && <span className="g-badge ok">edited by {r.editedBy}</span>}
                  </td>
                </tr>
  );

  if (error && !view) return <div className="g-empty">{error} <Button size="small" onClick={load}>Retry</Button></div>;
  if (!view) return <Skeleton active paragraph={{ rows: 10 }} />;

  const latest = !version || (versions[0] && versions[0].version === view.version);
  const canEdit = view.canEdit && latest;
  const s = view.summary || {};
  const byLevel = s.byLevel || {};

  // Guide 20.3, three short routes from a register: the size facet ("2-inch
  // and larger", across every system), a filtered table (its filter and the
  // rows it shows), a selection (its element keys).
  const newViewFrom = () => {
    const base = view.request || {};
    const keysOf = (list) => list.map((r) => r.elementKey).filter(Boolean);
    const only = (k) => Object.entries(filter || {}).every(([key, v]) => key === k || !v || !v.length);
    if (picked.length) {
      const sel = rows.filter((r) => picked.includes(r.id));
      return openNewView(history, projectId, { title: `${view.title}, selection of ${sel.length}`, request: { ...base, elementKeys: keysOf(sel) } });
    }
    const sizes = (filter && filter.sizes) || [];
    if (sizes.length === 1 && only("sizes")) {
      return openNewView(history, projectId, { title: `${sizes[0]} across every system`, request: { ...base, preset: "custom", systems: [], filter: { ...(base.filter || {}), size: sizes[0] } } });
    }
    const words = [
      ...((filter.levels || []).map((l) => levelName(manifest, l))),
      ...((filter.categories || [])), ...((filter.sizes || [])), ...((filter.bands || [])),
    ];
    return openNewView(history, projectId, {
      title: `${view.title}${words.length ? `, ${words.slice(0, 4).join(", ")}` : ""}`,
      request: {
        ...base,
        ...((filter.systems || []).length ? { systems: filter.systems } : {}),
        ...((filter.levels || []).filter((l) => l !== "not located").length ? { levels: filter.levels.filter((l) => l !== "not located") } : {}),
        ...((filter.zones || []).length ? { zones: filter.zones.map(Number).filter(Number.isInteger) } : {}),
        elementKeys: keysOf(visible),
      },
    });
  };

  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) message.success(ok); load(); if (onChanged) onChanged(); } catch (e) { message.error(e.message); }
    setBusy(false);
  };
  const selectRow = (id) => {
    const tr = tableRef.current && tableRef.current.querySelector(`tr[data-id="${id}"]`);
    if (tr) { tr.scrollIntoView({ block: "center", behavior: "smooth" }); tr.focus(); }
  };
  const hoverRun = (id, e) => {
    setHover(id);
    setTip(id && e ? { id, x: e.clientX, y: e.clientY } : null);
  };
  const tipRow = tip && rows.find((r) => r.id === tip.id);

  // ?print=1&sheet=<pageId>: the server's PDF job prints ONE tracking sheet
  // (guide 19.7) - only that page, which marks itself ready once drawn.
  const printSheet = (() => { try { const q = new URLSearchParams(window.location.search); return q.get("print") === "1" ? q.get("sheet") : null; } catch (e) { return null; } })();
  if (printSheet) {
    return <div className="g-view g-sheet-printview"><SheetPages projectId={projectId} manifest={manifest} view={view} statusByRow={null} picked={[]} onPick={() => {}} printPageId={printSheet} /></div>;
  }

  return (
    // data-render-ready: the view and its isometric have drawn - the signal
    // root-f5's render job waits on instead of a timeout.
    <div className="g-view" ref={rootRef} data-render-ready="1">
      <div className="g-viewhead">
        <div>
          <h1 className="g-title">{view.title}</h1>
          <p className="g-sub">
            Version {view.version}{view.createdByName ? `, ${view.source === "import" ? "published" : "generated"} by ${view.createdByName}` : ""} on {String(view.createdAt).slice(0, 10)}.
            {" "}<Tag color={view.status === "published" ? "green" : view.status === "in_review" ? "orange" : "default"}>{STATUS_LABEL[view.status]}</Tag>
            {view.review && view.review.by && view.status === "published" ? ` Published by ${view.review.by}.` : ""}
            {((view.builtFrom && view.builtFrom.documents) || []).length > 0 && (
              <span> Built from {view.builtFrom.documents.map((d, k) => (
                <span key={`${d.docId}-${k}`}>{k ? "; " : ""}{d.label || d.docId}{d.rev !== undefined && d.rev !== "" ? ` (rev ${d.rev})` : ""}{d.stage ? ` ${d.stage}` : ""}<VersionChip docId={d.docId} rev={Number(d.rev) || 0} /></span>
              ))}</span>
            )}
          </p>
        </div>
        <div className="g-actions g-noprint">
          {versions.length > 1 && (
            <Select size="small" value={view.version} style={{ width: 130 }} onChange={(v) => setVersion(v === versions[0].version ? null : v)}
                    options={versions.map((v) => ({
                      value: v.version,
                      // How much of the picture a regeneration changed (utils/glance/visualCheck).
                      label: `Version ${v.version}${v.version === versions[0].version ? " (latest)" : ""}${v.diff && v.diff.visual && v.diff.visual.changedPct != null ? ` · picture ${v.diff.visual.changedPct < 0.5 ? "unchanged" : `${Math.round(v.diff.visual.changedPct)}% changed`} from v${v.diff.visual.prevVersion}` : ""}`,
                    }))} />
          )}
          <Button size="small" icon={<PrinterOutlined />} onClick={() => printElement(rootRef.current, `${view.title} v${view.version}`)}>Print</Button>
          <Tooltip title="Summary, quantities by type, size and level (split by how each length was obtained), and the full schedule">
            <Button size="small" icon={<FileExcelOutlined />}
                    onClick={() => downloadAuthed(`/projects/${projectId}/glance/views/${encodeURIComponent(view.viewId)}/export.xlsx${exportQuery(version, shownIds)}`, `${view.viewId}-v${view.version}.xlsx`).catch((e) => message.error(e.message))}>
              Export Excel
            </Button>
          </Tooltip>
          <Button size="small" icon={<DownloadOutlined />} onClick={() => downloadCsv(projectId, view.viewId, version, shownIds).catch((e) => message.error(e.message))}>CSV</Button>
          <Contained name="PDF"><ViewPdfActions projectId={projectId} viewId={view.viewId} title={view.title} canSave={canEdit} /></Contained>
          <Contained name="kickoff pack"><MeetingPackButton projectId={projectId} view={{ viewId: view.viewId, version: view.version }} /></Contained>
          {view.canEdit && (picked.length > 0 || !isEmptyFilter(filter)) && (
            <Tooltip title={picked.length ? "A new view of the selected elements only" : "A new view of what this filter shows"}>
              <Button size="small" onClick={newViewFrom}>New view from {picked.length ? "the selection" : "this filter"}</Button>
            </Tooltip>
          )}
          {canEdit && (
            <>
              <Dropdown menu={{
                items: [
                  { key: "draft", label: "Back to draft", disabled: view.status === "draft" },
                  { key: "in_review", label: "Send for review", disabled: view.status === "in_review" },
                  { key: "published", label: "Publish", disabled: view.status === "published" },
                ],
                onClick: ({ key }) => act(() => viewsApi(projectId, `/${view.viewId}/status`, { method: "POST", body: { status: key } }), STATUS_LABEL[key]),
              }}>
                <Button size="small" loading={busy}>Status <DownOutlined /></Button>
              </Dropdown>
              <Tooltip title="Writes an explanation for every row without one, and lists conflicts and scope questions. It never changes lengths, endpoints or quantities; everything it writes is marked for review.">
                <Button size="small" onClick={() => act(async () => {
                  const out = await viewsApi(projectId, `/${view.viewId}/explain`, { method: "POST" });
                  message.info(`Explaining ${out.rows} rows. Refresh in a few minutes.`);
                })}>Explain rows</Button>
              </Tooltip>
              {view.source === "generate" && (
                <Button size="small" icon={<ReloadOutlined />} type={view.stale ? "primary" : "default"}
                        onClick={() => act(async () => {
                          const out = await viewsApi(projectId, `/${view.viewId}/regenerate`, { method: "POST" });
                          const d = out.diff || {};
                          message.success(`Version ${out.version}: ${(d.added || []).length} added, ${(d.removed || []).length} removed, ${(d.changed || []).length} changed${d.carriedEdits ? `, ${d.carriedEdits} edits kept` : ""}`);
                        })}>Regenerate</Button>
              )}
              <RemoveViewButton show={!!view.canDelete}
                                onConfirm={() => act(async () => { await viewsApi(projectId, `/${view.viewId}`, { method: "DELETE" }); if (onDeleted) onDeleted(); }, "Removed")} />
            </>
          )}
        </div>
      </div>

      {view.stale && latest && (
        <div className="g-callout">
          <b>This view is stale.</b> The building data it was built from (version {view.builtFrom && view.builtFrom.manifestVersion}) has been replaced by version {view.manifestVersion}.
          {canEdit ? " Regenerate it to see what changed; edited rows are kept." : ""}
        </div>
      )}
      {!latest && <div className="g-callout">You are looking at version {view.version}, not the latest.</div>}

      <div className="g-strip g-strip-static">
        <div><b>{(s.runs || 0).toLocaleString()} runs</b><span>{(s.qty || 0).toLocaleString()} counting multiples</span></div>
        <div><b>{(s.knownLengthFt || 0).toLocaleString()} ft</b><span>Measured and estimated length, excluding unlocated runs</span></div>
        {Object.entries(byLevel).filter(([k]) => k !== "unlocated").slice(0, 5).map(([k, b]) => (
          <div key={k}><b>{b.runs} on {levelName(manifest, k)}</b><span>{b.lengthFt.toLocaleString()} ft</span></div>
        ))}
        {s.unlocated > 0 && <div><b>{s.unlocated} not located</b><span>Listed, but the documents do not place them</span></div>}
      </div>

      {(view.caveats || []).length > 0 && (
        <div className="g-callout"><b>How much of this is measured.</b> {view.caveats.join(". ")}.</div>
      )}

      {view.diff && latest && ((view.diff.added || []).length + (view.diff.removed || []).length + (view.diff.changed || []).length) > 0 && (
        <details className="g-details">
          <summary>
            Changes from version {view.supersedes}: {(view.diff.added || []).length} added, {(view.diff.removed || []).length} removed, {(view.diff.changed || []).length} changed
            {view.diff.carriedEdits ? `, ${view.diff.carriedEdits} edited rows kept` : ""}
          </summary>
          <ul>
            {(view.diff.changed || []).slice(0, 30).map((c) => (
              <li key={c.id}>{c.id}: {c.fields.map((f) => `${f.field} ${f.from == null || f.from === "" ? "blank" : f.from} to ${f.to == null || f.to === "" ? "blank" : f.to}`).join(", ")}</li>
            ))}
            {(view.diff.added || []).length > 0 && <li>Added: {view.diff.added.join(", ")}</li>}
            {(view.diff.removed || []).length > 0 && <li>Removed (previous ids): {view.diff.removed.join(", ")}</li>}
          </ul>
        </details>
      )}

      {(view.flags || []).filter((f) => f.review !== "rejected").length > 0 && (
        <section>
          <h2>Conflicts and scope questions</h2>
          {(view.flags || []).map((f, i) => (f.review === "rejected" ? null : (
            <div key={i} className="g-flag">
              <Tag color={f.kind === "conflict" ? "red" : f.kind === "missing" ? "gold" : "blue"}>{f.kind}</Tag>
              {f.text}
              <span className="g-tsub"> Rows {f.rows.map((id) => <button key={id} type="button" className="g-linkbtn" onClick={() => selectRow(id)} onMouseEnter={() => setHover(id)} onMouseLeave={() => setHover(null)}>{id}</button>)}
                {(f.sources || []).length ? `; ${f.sources.join(", ")}` : ""}</span>
              {f.by === "claude" && f.review === "pending" && <span className="g-badge" title="Raised automatically; not yet reviewed">Pending</span>}
              {(f.checklists || []).map((c) => (
                <Tooltip key={c.id} title={`Checklist item: ${c.title}${c.dueDate ? `, due ${String(c.dueDate).slice(0, 10)}` : ""}`}>
                  <a href="/checklists" className="g-tsub" style={{ marginLeft: 8 }}>
                    <Tag color={CHECK_COLOR[c.status] || "default"}>{CHECK_LABEL[c.status] || c.status}</Tag>
                  </a>
                </Tooltip>
              ))}
              {canEdit && f.review === "pending" && (
                <a className="g-tsub" href={`/approval-queue?projectId=${projectId}&tab=documentation`}> Decide it in the Review Center</a>
              )}
              {f.issueId ? (
                <Tag color="blue" style={{ marginLeft: 8 }}>{f.issueLabel || "Issue"}</Tag>
              ) : canEdit && (
                // Guide 18.7: an issue or an RFI from the flag; its rows are blocked until it closes.
                <span className="g-actions" style={{ marginLeft: 8 }}>
                  <button type="button" className="g-linkbtn" onClick={() => act(async () => {
                    const out = await viewsApi(projectId, `/${view.viewId}/flags/${i}/issue`, { method: "POST", body: {} });
                    message.success(`${out.label || "Issue"} made${out.blocked ? `; ${out.blocked} row${out.blocked === 1 ? "" : "s"} blocked until it closes` : ""}`);
                  })}>Make an issue</button>
                  <button type="button" className="g-linkbtn" onClick={() => act(async () => {
                    const out = await viewsApi(projectId, `/${view.viewId}/flags/${i}/issue`, { method: "POST", body: { rfi: true } });
                    message.success(`${out.label || "RFI"} made${out.blocked ? `; ${out.blocked} row${out.blocked === 1 ? "" : "s"} blocked until it is answered` : ""}`);
                  })}>Make an RFI</button>
                </span>
              )}
            </div>
          )))}
        </section>
      )}

      <FacetBar rows={rows} filter={filter} onChange={setFilter} ctx={facetCtx} levelName={(l) => (l === "not located" ? l : levelName(manifest, l))} />
      {rows.length > 0 && visible.length === 0 && <div className="g-empty">{emptyReason(rows, filter)}</div>}

      <section>
        <h2>Isometric by level</h2>
        <p className="g-note">
          Levels are pulled apart vertically. {view.context && view.context.note ? `${view.context.note}. ` : ""}
          Hover a run or a table row to link them; click a run to find its row.
        </p>
        <div className="g-legend">
          {types.map((t) => {
            const sample = rows.find((r) => r.type === t);
            return <span key={t}><i style={{ background: runColor(sample) }} />{t}{termOf(t) ? <GlossaryTerm id={termOf(t)} /> : null}</span>;
          })}
        </div>
        <div className="g-svgwrap">
          <RegisterIsometric manifest={manifest} view={view} visibleIds={visibleIds} highlightId={hover} onHover={hoverRun} onSelect={selectRow} />
        </div>
      </section>

      <section>
        <h2>Schedule</h2>
        <p className="g-note">{(() => { const t = tally(visible); return `${t.rows} runs shown${isEmptyFilter(filter) ? "" : ` of ${rows.length}`}, ${t.qty} counting multiples, about ${t.lengthFt.toLocaleString()} ft where length is known.`; })()}</p>
        <div className="g-tablewrap g-tall" ref={tableRef}>
          <table>
            <thead>
              <tr>
                <th>ID</th><th>Level</th><th>Type</th>{staged && <><th>Stage (as marked)</th><th>Status</th></>}<th className="g-num">Qty</th><th>Height</th><th className="g-num">Length each (ft)</th>
                <th className="g-num">Total (ft)</th><th>From</th><th>To</th><th>Keynotes</th><th>Source</th><th>Explanation</th>
              </tr>
            </thead>
            <tbody>
              {groupRows(visible, openGroups).map((e) => (e.kind === "group" ? (
                <tr key={e.key} className="g-group">
                  <td>
                    <button type="button" className="g-linkbtn" aria-expanded={openGroups.has(e.key)}
                            onClick={() => setOpenGroups((o) => { const n = new Set(o); if (n.has(e.key)) n.delete(e.key); else n.add(e.key); return n; })}>
                      {openGroups.has(e.key) ? "Hide" : "Show"} {e.rows.length}
                    </button>
                  </td>
                  <td>{rowLevel(manifest, e.rows[0])}</td>
                  <td>{e.type}{e.rows[0].size ? <div className="g-tsub">{e.rows[0].size}</div> : null}</td>
                  {staged && <><td /><td /></>}
                  <td className="g-num">{e.qty}</td>
                  <td>{e.rows[0].height}{ceilingText(e.rows[0]) ? <div className="g-tsub">{ceilingText(e.rows[0])}</div> : null}</td>
                  <td className="g-num"><div className="g-basis">{e.rows[0].lengthNote || "Not measured"}</div></td>
                  <td className="g-num" />
                  <td>{e.rows[0].from}</td>
                  <td>{e.rows.length} units{e.rows[0].serves ? <div className="g-tsub">Serves {e.rows[0].serves}</div> : null}</td>
                  <td />
                  <td>{[...new Set(e.rows.flatMap((r) => (r.sources || []).map((x) => x.sheet || x.docId)))].join(", ")}</td>
                  <td className="g-expl">{e.rows[0].explanation}</td>
                </tr>
              ) : rowTr(e.row, e.child)))}
            </tbody>
          </table>
        </div>
      </section>

      <Contained name="sheets"><SheetPages projectId={projectId} manifest={manifest} view={view} statusByRow={statusByRow} picked={picked} onPick={setPicked} canEdit={canEdit} reloadKey={liveTick} /></Contained>

      <TrackingPanel projectId={projectId} manifest={manifest} view={view} rows={visible} filter={filter} onFilter={setFilter} onStates={setStatusByRow} canEdit={canEdit} canAdd={!!(view.canAdd && latest)} canDelete={!!(view.canDelete && latest)} picked={picked} onPicked={setPicked} reloadKey={liveTick} />

      <ReconcilePanel projectId={projectId} view={view} canEdit={canEdit} onApplied={() => { load(); if (onChanged) onChanged(); }} />

      <Contained name="estimate"><EstimatePanel projectId={projectId} view={{ viewId: view.viewId, version: view.version }} /></Contained>

      {tipRow && (
        <div className="g-runtip" style={{ left: Math.min(tip.x + 16, window.innerWidth - 470), top: Math.min(tip.y + 16, window.innerHeight - 200) }}>
          <b>{tipRow.id}</b> {tipRow.type}<br />
          {rowLevel(manifest, tipRow)}, {tipRow.qty} x {tipRow.size || "size not stated"}{tipRow.height ? `, ${tipRow.height}` : ""}<br />
          From {tipRow.from || "n/a"} to {tipRow.to || "n/a"}<br />
          Length {tipRow.lengthFt != null ? `${fmtFt(tipRow.lengthFt)} ft each (${(BASIS_LABEL[tipRow.lengthBasis] || "").toLowerCase()})` : "not measured"}
        </div>
      )}

      <ModelLinkModal projectId={projectId} viewId={view.viewId} row={linking} canAdd={!!(view.canAdd && latest)} canDelete={!!(view.canDelete && latest)} open={!!linking}
                      onClose={() => setLinking(null)} onChanged={load} />

      <EditRow row={editing} onCancel={() => setEditing(null)}
               onSave={(changes) => {
                 const row = editing;
                 setEditing(null);
                 if (!Object.keys(changes).length) return;
                 act(() => viewsApi(projectId, `/${view.viewId}/rows/${encodeURIComponent(row.id)}`, { method: "PATCH", body: changes }), `${row.id} saved`);
               }} />
    </div>
  );
}
